import React from 'react';
import { PostField, PostFieldList } from '.';
import { FieldSectionWrapper } from './FieldSectionWrapper';
import { useGetWalmartFormItemError } from '../hooks';

import './styles.less';
import { filterOptionHandler } from './utils';

const PostFieldRecursive = React.memo((props) => {
  const {
    type,
    childProperties,
    items,
    title,
    fullKey,
    fieldKey,
    className,
    format,
    minItems,
    maxItems,
    enums,
    lowerLimit,
    upperLimit,
    description,
    isRequiredProperty,
    //* additional
    fieldListItemProps, //* field in fields of form
    formItemStyle, //* add custom style to form item
    labelCol, //* adjust label col
    wrapperCol, //* adjust wrapper col
    productId,
    showServerError = true,
    errorKey,
    name,
    filterOption,
    isRivirMappingField,
    rivirMappingField,
    multipleOf,
  } = props || {};

  const formItemServerError = useGetWalmartFormItemError({
    productId,
    errorKey: errorKey || fullKey,
  });

  if (type === 'object') {
    return (
      <FieldSectionWrapper title={title}>
        {childProperties?.map((fieldItem) => {
          const { key, enums, ...restFieldItemProps } = fieldItem;
          return (
            <PostFieldRecursive
              key={fieldItem?.fullKey}
              {...restFieldItemProps}
              fieldKey={key}
              productId={productId}
              showServerError={showServerError}
              filterOption={filterOptionHandler(enums, fullKey)}
              enums={enums}
            />
          );
        })}
      </FieldSectionWrapper>
    );
  }

  if (type === 'array') {
    //* multiple selection
    if (items.type === 'string' && items?.enums?.length > 0) {
      return (
        <PostField
          fieldKey={fieldKey}
          fullKey={fullKey}
          type='multi-select'
          label={title}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          fieldListItemProps={fieldListItemProps}
          formItemStyle={formItemStyle}
          className={className}
          enums={items.enums}
          isRequiredProperty={isRequiredProperty}
          serverError={formItemServerError}
          showServerError={showServerError}
          name={name}
          minItems={minItems}
          ruleType='list'
          filterOption={filterOptionHandler(items.enums, fullKey)}
          isRivirMappingField={isRivirMappingField}
          rivirMappingField={rivirMappingField}
          step={multipleOf}
        />
      );
    }

    //* multiple image string
    if (items?.format === 'uri') {
      return (
        <PostField
          fieldKey={fieldKey}
          fullKey={fullKey}
          type='product-image-url-multiple'
          label={title}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          fieldListItemProps={fieldListItemProps}
          formItemStyle={formItemStyle}
          className={className}
          description={description}
          isRequiredProperty={isRequiredProperty}
          serverError={formItemServerError}
          showServerError={showServerError}
          name={name}
          minItems={minItems}
          ruleType='list'
          productId={productId}
          isRivirMappingField={isRivirMappingField}
          rivirMappingField={rivirMappingField}
          step={multipleOf}
        />
      );
    }

    return (
      <div>
        <PostFieldList
          fullKey={fullKey}
          fieldName={title}
          label={title}
          items={items}
          minItems={minItems}
          isRequiredProperty={isRequiredProperty}
          productId={productId}
          serverError={formItemServerError}
          description={description}
          filterOption={filterOption}
          isRivirMappingField={isRivirMappingField}
          rivirMappingField={rivirMappingField}
          step={multipleOf}
        />
      </div>
    );
  } //* end type array

  if (type === 'string') {
    if (format === 'uri') {
      return (
        <PostField
          fieldKey={fieldKey}
          fullKey={fullKey}
          type='product-image-url'
          label={title}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          fieldListItemProps={fieldListItemProps}
          formItemStyle={formItemStyle}
          className={className}
          description={description}
          isRequiredProperty={isRequiredProperty}
          serverError={formItemServerError}
          showServerError={showServerError}
          name={name}
          productId={productId}
          isRivirMappingField={isRivirMappingField}
          rivirMappingField={rivirMappingField}
        />
      );
    }

    if (format === 'date-time') {
      return (
        <PostField
          fieldKey={fieldKey}
          fullKey={fullKey}
          type='dateTime'
          label={title}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          fieldListItemProps={fieldListItemProps}
          formItemStyle={formItemStyle}
          className={className}
          description={description}
          isRequiredProperty={isRequiredProperty}
          serverError={formItemServerError}
          showServerError={showServerError}
          isRivirMappingField={isRivirMappingField}
          rivirMappingField={rivirMappingField}
        />
      );
    }

    //* single selection (type = 'string' and !!enum)
    if (enums?.length > 0) {
      return (
        <PostField
          fieldKey={fieldKey}
          fullKey={fullKey}
          type='select'
          label={title}
          labelCol={labelCol}
          wrapperCol={wrapperCol}
          fieldListItemProps={fieldListItemProps}
          formItemStyle={formItemStyle}
          className={className}
          enums={enums}
          description={description}
          isRequiredProperty={isRequiredProperty}
          serverError={formItemServerError}
          showServerError={showServerError}
          name={name}
          filterOption={filterOptionHandler(enums, fullKey)}
          isRivirMappingField={isRivirMappingField}
          rivirMappingField={rivirMappingField}
        />
      );
    }

    //* default text input
    return (
      <PostField
        fieldKey={fieldKey}
        fullKey={fullKey}
        type='string'
        label={title}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
        fieldListItemProps={fieldListItemProps}
        formItemStyle={formItemStyle}
        className={className}
        description={description}
        minLength={lowerLimit}
        maxLength={upperLimit}
        isRequiredProperty={isRequiredProperty}
        serverError={formItemServerError}
        showServerError={showServerError}
        name={name}
        isRivirMappingField={isRivirMappingField}
        rivirMappingField={rivirMappingField}
      />
    );
  }

  if (type === 'number') {
    return (
      <PostField
        fieldKey={fieldKey}
        fullKey={fullKey}
        type='number'
        label={title}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
        fieldListItemProps={fieldListItemProps}
        className={className}
        min={lowerLimit}
        max={upperLimit}
        description={description}
        isRequiredProperty={isRequiredProperty}
        serverError={formItemServerError}
        showServerError={showServerError}
        name={name}
        isRivirMappingField={isRivirMappingField}
        rivirMappingField={rivirMappingField}
        step={multipleOf}
      />
    );
  }

  if (type === 'integer') {
    return (
      <PostField
        fieldKey={fieldKey}
        fullKey={fullKey}
        type='integer'
        label={title}
        labelCol={labelCol}
        wrapperCol={wrapperCol}
        fieldListItemProps={fieldListItemProps}
        className={className}
        description={description}
        isRequiredProperty={isRequiredProperty}
        serverError={formItemServerError}
        showServerError={showServerError}
        name={name}
        isRivirMappingField={isRivirMappingField}
        rivirMappingField={rivirMappingField}
        step={multipleOf}
      />
    );
  }
});

export { PostFieldRecursive };
